/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import moment, { Duration } from 'moment';
import { AggregationInterval } from '../shared/entities/dataUsage';
import { State as IDatePickerState } from '../store/datePicker/reducer';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from './StringUtils';

const LOCAL_DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm:ss [UTC]';
const DOTTED_DATE_FORMAT = 'MM.DD.YYYY';
const DOTTED_DATE_YYYYMMDD = 'YYYY.MM.DD';
export const MINUTES_IN_DAY = 1440;
export const MINUTES_IN_HOUR = 60;
export const MINUTES_IN_HALF_DAY = MINUTES_IN_HOUR * 12;
export const MINUTES_IN_FOUR_HOURS = MINUTES_IN_HOUR * 4;
export const MINUTES_IN_TWO_HOURS = MINUTES_IN_HOUR * 2;
export const MINUTES_IN_HALF_HOUR = MINUTES_IN_HOUR * 0.5;
export const SEC_TO_MS_CONVERSION = 1000;

export function todayAsString(): string {
  return moment().format(LOCAL_DATE_FORMAT);
}

export function timeAsString(): string {
  return moment().utc().format(TIME_FORMAT);
}

export function hasDateUpdated(previousDatePicker: IDatePickerState, currentDatePicker: IDatePickerState): boolean {
  return !!(
    currentDatePicker.startDate !== previousDatePicker.startDate ||
    currentDatePicker.endDate !== previousDatePicker.endDate
  );
}

// Convert a time in seconds since the epoch to a Date.
export function secondsSinceEpochToDate(secSinceEpoch: number): Date {
  return moment.unix(secSinceEpoch).toDate();
}

// Return the number of minutes that have passed between the present and the
// given time in seconds since the epoch.
export function durationToNowInMin(secSinceEpoch: number): number {
  return moment.duration(moment().diff(moment.unix(secSinceEpoch))).asMinutes();
}

// Return the number of days that have passed between the present and the
// given time in seconds since the epoch.
export function daysUntilNow(secSinceEpoch: number): number {
  return moment.duration(moment().diff(moment.unix(secSinceEpoch))).asDays();
}

export function durationFromDateUntilNowHoursMinutesSeconds(startDate: number): string {
  let duration: any;
  duration = moment.duration(moment().diff(moment.unix(startDate)));
  if (Math.floor(duration.asHours())) {
    return `${Math.floor(duration.asHours())}h ${String(Math.floor(duration.asMinutes() % 60)).padStart(2, '0')}m`;
  }
  if (Math.floor(duration.asMinutes())) {
    return `${Math.floor(duration.asMinutes())}m`;
  }
  return `less than a minute`;
}

export function minutesBetweenDates(otherDate: number, thisDate: number): number {
  let duration: Duration;
  duration = moment.duration(moment.unix(thisDate).diff(moment.unix(otherDate)));
  return duration.asMinutes();
}

export function formatTtho(tthoSeconds: number) {
  const date = moment.unix(tthoSeconds).utc();
  return date.hours() ? `${date.hours()} h ${String(date.minutes()).padStart(2, '0')} m` : `${date.minutes()} m`;
}

export function formatMissionPlanCreatedDate(createdDate: string) {
  const date = moment(createdDate, 'MM-DD-YY');
  if (!date.isValid()) {
    // Moment returns the string 'Invalid date' if the date cannot be parsed. Instead of that text we display '--'
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  }
  return date.utc(true).format(DOTTED_DATE_FORMAT);
}

export function formatDottedDateYYYYMMDD(dateStr: string) {
  const date = moment(dateStr);
  if (!date.isValid()) {
    // Moment returns the string 'Invalid date' if the date cannot be parsed. Instead of that text we display '--'
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  }
  return date.utc().format(DOTTED_DATE_YYYYMMDD);
}

export function formatPOExportMissoinCreatedDate(createdDate: string) {
  const date = moment(createdDate);
  if (!date.isValid()) {
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  }
  return date.utc().format('ddd MMM Do YYYY, HH:mm:ss');
}
export function compareMissionPlanCreatedDates(createdDate1: string, createdDate2: string): number {
  const date1 = moment(createdDate1, DOTTED_DATE_FORMAT);
  const date2 = moment(createdDate2, DOTTED_DATE_FORMAT);

  //Edge case to handle invalid dates (brings them to bottom when sorted ascending)
  if (!date1.isValid()) return 1;
  if (!date2.isValid()) return -1;

  //Compare the valid dates
  if (date1 > date2) {
    return 1;
  } else if (date2 > date1) {
    return -1;
  } else {
    return 0;
  }
}

export function minutesToFormattedMinutesString(minutes: number) {
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function minutesToFormattedMinutesStringColonDelimitedFormat(minutes: number) {
  const hours = Math.floor(minutes / 60);
  return `${hours.toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
}

export function secondsToFormattedHoursMinutesSecondsColonDelimitedFormat(seconds: number) {
  // Convert seconds to hh:mm:ss format
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const sec = Math.floor(seconds % 60);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${sec
    .toString()
    .padStart(2, '0')}`;
}

/**
 * Returns a string formatted from a date object into a UTC TimeStamp HH:MM:ss.
 * Replaces Moment because creating a Moment object is expensive.
 * This allows you to use a single date object to format through multiple functions.
 * @param date {Date} - standard JS Date Object
 */
export function dateToUTCTime(date: Date, format: 'HH:mm:ss' | 'HH:mm' = 'HH:mm:ss') {
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  if (format === 'HH:mm') return `${hours}:${minutes}`;
  if (format === 'HH:mm:ss') return `${hours}:${minutes}:${seconds}`;
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Returns a string formatted from a date object into a UTC DateStamp MM:DD:YYYY
 * Replaces Moment because creating a Moment object is expensive.
 * This allows you to use a single date object to format through multiple functions.
 * @param date {Date} - standard JS Date Object
 */
export function dateToUTCDate(date: Date, dotted = false) {
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear().toString();
  return dotted ? `${month}.${day}.${year}` : `${month}/${day}/${year}`;
}

/**
 * Returns a string representing the date formatted as <monthDay> <monthName> <year>, <timeUtc> in UTC time
 *
 * ex: 21 May 2020, 05:22:52
 *
 * Note: Uses 'en-GB' locale in order to use 00 for the 24th hour instead of 24
 *
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 * @param date {Date} - standard JS Date Object
 */
export function dateToUTCDateFullDayName(date: Date): string {
  const monthName: string = date.toLocaleString('en-GB', {
    month: 'short',
    hour12: false,
    timeZone: 'UTC',
  });
  const day: string = date.toLocaleString('en-GB', {
    day: 'numeric',
    hour12: false,
    timeZone: 'UTC',
  });
  const year: string = date.toLocaleString('en-GB', {
    year: 'numeric',
    hour12: false,
    timeZone: 'UTC',
  });
  const time: string = date.toLocaleString('en-GB', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${day} ${monthName} ${year}, ${time}`;
}

/**
 * Returns a string representing the date formatted as <monthDay> <monthName> <year>
 *
 * ex: 21 May 2020
 *
 * Note: Uses 'en-GB' locale in order to use 00 for the 24th hour instead of 24
 *
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 * @param date {Date} - standard JS Date Object
 */
export function dateToUTCDateFullDayNameWithoutTime(date: Date): string {
  const monthName: string = date.toLocaleString('en-GB', {
    month: 'short',
    hour12: false,
    timeZone: 'UTC',
  });
  const day: string = date.toLocaleString('en-GB', {
    day: 'numeric',
    hour12: false,
    timeZone: 'UTC',
  });
  const year: string = date.toLocaleString('en-GB', {
    year: 'numeric',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${day} ${monthName} ${year}`;
}

/**
 * returns true if the date range is within the selected report period(Monthly, weekly and daily), otherwise false.
 *
 * @param startDate
 * @param endDate
 * @param interval
 *
 */
export const dateRangeValidator = (startDate: Date, endDate: Date, interval: AggregationInterval): boolean => {
  let isValidInterval = false;
  //milliseconds in a day
  const dayInMilliseconds = 24 * 3600 * 1000;
  const dayDiffInMillisecconds = endDate.getTime() - startDate.getTime();
  const numOfDays = dayDiffInMillisecconds / dayInMilliseconds;

  if (interval === 'YEAR') {
    isValidInterval = numOfDays >= 365;
  } else if (interval === 'MONTH') {
    isValidInterval = numOfDays >= 30;
  } else if (interval === 'WEEK') {
    isValidInterval = numOfDays >= 7;
  } else if (interval === 'DAY') {
    isValidInterval = numOfDays >= 1;
  }

  return isValidInterval;
};
